import { useGetElementDetail } from '@vindr/services/actions/element.action';
import useRefreshComponent from '@vindr/utils/hooks/useRefreshComponent';
import { Checkbox, Collapse, Tooltip } from 'antd';
import { FC, useEffect, useState } from 'react';
import InputComponentElement from '../../../InputComponentElement';
import StructuredElement from './StructuredElement';
import { QuestionCircleOutlined } from '@ant-design/icons';

interface StructureInputProps {
  element: StructuredElement;
  onChange: (val: any, element: StructuredElement, preventHighlight?: boolean) => void;
  disabled: boolean;
  index?: number;
}

const { Panel } = Collapse;

const StructureInput: FC<StructureInputProps> = ({ element, onChange, disabled, index }) => {
  const [refresh, setRefresh] = useRefreshComponent();
  const [isHidden, setIsHidden] = useState(element.is_hidden);
  const { data, isFetching } = useGetElementDetail(
    { id: element.id },
    { enabled: element.elements.length === 0 && !!element.linked_element_ids?.length }
  );

  useEffect(() => {
    element.setIsHidden(isHidden);
    onChange(element.value, element);
    setRefresh();
  }, [element, isHidden, onChange, setRefresh]);

  useEffect(() => {
    if (!isFetching && data?.elements?.length) {
      onChange(element.value, element, true);
      const mergeElements = data.elements.map((x, index) => ({
        ...x,
        ...element.elements[index],
      }));
      element.setElements(mergeElements);
      setRefresh();
    }
  }, [data, element, isFetching, setRefresh]);

  return (
    <Collapse
      key={refresh}
      bordered={false}
      accordion
      className="ml-[-8px] [&_.ant-collapse-header]:py-2"
    >
      <Panel
        key={element.id}
        header={
          <div className="inline-flex">
            <Checkbox
              checked={!isHidden}
              className="mr-2 mt-0.5"
              onChange={(e) => setIsHidden(!e.target.checked)}
            />
            <span>{[element?.name, index ?? ''].join(' ')}</span>

            {element?.tooltip && (
              <Tooltip title={element?.tooltip}>
                <QuestionCircleOutlined className="ml-1 text-[#ffffff73]" />
              </Tooltip>
            )}
          </div>
        }
        forceRender
      >
        {element.elements.map(
          (childElement) =>
            childElement && (
              <InputComponentElement
                key={`${childElement?.level}-${childElement?.id}`}
                element={childElement}
                onChange={onChange}
                disabled={disabled}
              />
            )
        )}
      </Panel>
    </Collapse>
  );
};

export default StructureInput;
